import {
  closestPointOnSegment,
  dist,
  intersectLines,
  polar,
  pt,
  type Box,
  type Pt,
} from '../model/geom';
import {
  entitySegments,
  entitySnapPoints,
  type SnapCandidate,
  type SnapKind,
} from '../model/entity';
import { normalizeAngle, type Micron } from '../model/units';
import type { Scene } from '../scene';
import type { Viewport } from '../render/viewport';

/**
 * 스냅 엔진 (작업지시서 P2-07)
 *
 * 정밀 배치의 전부다. 이게 없으면 매대를 벽에 붙일 수 없다.
 *
 * 설계 요점:
 *   · 조리개(aperture)는 **화면 픽셀** 기준이다. 줌 배율이 바뀌어도
 *     "커서 근처"의 체감이 일정해야 하기 때문. 월드 단위로 잡으면
 *     확대했을 때 아무것도 안 잡히고 축소했을 때 다 잡힌다.
 *   · 우선순위가 있다. 끝점과 중점이 동시에 조리개 안에 들어오면
 *     AutoCAD 는 끝점을 고른다. 그 순서를 그대로 따른다.
 *   · 직교(Ortho)/극좌표(Polar)는 스냅과 별개 축이며, 객체스냅보다 먼저 적용된다.
 */

/** 낮을수록 우선. AutoCAD 의 실질 우선순위를 따른다. */
const PRIORITY: Record<SnapKind, number> = {
  endpoint: 1,
  intersection: 2,
  midpoint: 3,
  center: 4,
  quadrant: 5,
  insertion: 6,
  perpendicular: 7,
  nearest: 9,
};

export const ALL_SNAP_KINDS: SnapKind[] = [
  'endpoint',
  'midpoint',
  'center',
  'quadrant',
  'intersection',
  'perpendicular',
  'insertion',
  'nearest',
];

export interface SnapSettings {
  /** F3 — 객체 스냅 */
  osnap: boolean;
  /** F8 — 직교 모드 */
  ortho: boolean;
  /** F10 — 극좌표 추적 */
  polar: boolean;
  /** F9 — 격자 스냅 */
  gridSnap: boolean;
  /** 격자 스냅 간격 (µm) */
  gridStep: Micron;
  /** 극좌표 추적 각도 (도) */
  polarAngleDeg: number;
  /** 켜져 있는 객체스냅 종류 */
  kinds: Set<SnapKind>;
  /** 조리개 반경 (화면 px) */
  aperturePx: number;
}

export function defaultSnapSettings(): SnapSettings {
  return {
    osnap: true,
    ortho: false,
    polar: false,
    gridSnap: false,
    gridStep: 100_000, // 100mm
    polarAngleDeg: 45,
    kinds: new Set<SnapKind>([
      'endpoint',
      'midpoint',
      'center',
      'quadrant',
      'intersection',
      'perpendicular',
    ]),
    aperturePx: 12,
  };
}

export interface SnapResult {
  point: Pt;
  /** 화면에 마커를 그릴 종류. null 이면 스냅 없음(자유 이동). */
  kind: SnapKind | null;
  /** 스냅이 걸린 엔티티 (하이라이트용) */
  entityId?: string;
  /** 직교/극좌표 추적선 표시 여부 */
  tracking?: { from: Pt; angleRad: number } | null;
}

/**
 * 커서 위치를 스냅된 점으로 바꾼다.
 *
 * 적용 순서 (AutoCAD 와 동일):
 *   1. 객체 스냅 — 가장 강하다. 걸리면 직교/극좌표를 무시한다.
 *   2. 직교(Ortho) 또는 극좌표(Polar) — 기준점이 있을 때만
 *   3. 격자 스냅
 *   4. 자유 이동
 */
export function resolveSnap(
  scene: Scene,
  vp: Viewport,
  cursor: Pt,
  base: Pt | null,
  s: SnapSettings
): SnapResult {
  // 1. 객체 스냅
  if (s.osnap && s.kinds.size > 0) {
    const hit = findObjectSnap(scene, vp, cursor, base, s);
    if (hit) return hit;
  }

  // 2. 직교 / 극좌표 (기준점 필요)
  if (base) {
    if (s.ortho) {
      const p = constrainOrtho(base, cursor);
      return {
        point: p,
        kind: null,
        tracking: { from: base, angleRad: Math.atan2(p.y - base.y, p.x - base.x) },
      };
    }
    if (s.polar) {
      const p = constrainPolar(base, cursor, s.polarAngleDeg);
      if (p) {
        return {
          point: p,
          kind: null,
          tracking: { from: base, angleRad: Math.atan2(p.y - base.y, p.x - base.x) },
        };
      }
    }
  }

  // 3. 격자 스냅
  if (s.gridSnap && s.gridStep > 0) {
    return {
      point: pt(
        Math.round(cursor.x / s.gridStep) * s.gridStep,
        Math.round(cursor.y / s.gridStep) * s.gridStep
      ),
      kind: null,
    };
  }

  // 4. 자유
  return { point: cursor, kind: null };
}

/* -------------------------------------------------------------------------- *
 * 객체 스냅
 * -------------------------------------------------------------------------- */

function apertureBox(cursor: Pt, radius: Micron): Box {
  return {
    minX: cursor.x - radius,
    minY: cursor.y - radius,
    maxX: cursor.x + radius,
    maxY: cursor.y + radius,
  };
}

function findObjectSnap(
  scene: Scene,
  vp: Viewport,
  cursor: Pt,
  base: Pt | null,
  s: SnapSettings
): SnapResult | null {
  const radius = vp.pxToWorld(s.aperturePx);
  const box = apertureBox(cursor, radius);

  const near = scene
    .queryBox(box)
    .filter((e) => scene.isVisible(e) && !scene.isLocked(e));

  if (near.length === 0) return null;

  let best: { c: SnapCandidate; d: number } | null = null;
  const consider = (c: SnapCandidate) => {
    if (!s.kinds.has(c.kind)) return;
    const d = dist(cursor, c.point);
    if (d > radius) return;
    if (
      !best ||
      PRIORITY[c.kind] < PRIORITY[best.c.kind] ||
      (PRIORITY[c.kind] === PRIORITY[best.c.kind] && d < best.d)
    ) {
      best = { c, d };
    }
  };

  // 엔티티가 직접 제공하는 스냅점 (끝점/중점/중심/사분점/삽입점)
  for (const e of near) {
    for (const c of entitySnapPoints(e, scene.blocks)) consider(c);
  }

  // 교차점 — 엔티티 쌍이 필요하므로 별도 계산.
  // 조리개 안 엔티티가 많으면 조합이 폭발하므로 상한을 둔다.
  if (s.kinds.has('intersection') && near.length > 1 && near.length <= 12) {
    const segsOf = near.map((e) => ({ id: e.id, segs: entitySegments(e, scene.blocks) }));
    outer: for (let i = 0; i < segsOf.length; i++) {
      for (let j = i + 1; j < segsOf.length; j++) {
        for (const [a1, a2] of segsOf[i]!.segs) {
          for (const [b1, b2] of segsOf[j]!.segs) {
            const x = intersectLines(a1, a2, b1, b2, true);
            if (x && dist(cursor, x) <= radius) {
              consider({ kind: 'intersection', point: x, entityId: segsOf[i]!.id });
              // 첫 교차를 찾으면 충분하다. 계속 돌면 비싸진다.
              continue outer;
            }
          }
        }
      }
    }
  }

  // 수직 — 기준점이 있을 때만 의미가 있다
  if (s.kinds.has('perpendicular') && base) {
    for (const e of near) {
      for (const [a, b] of entitySegments(e, scene.blocks)) {
        const foot = closestPointOnSegment(base, a, b, true);
        if (dist(cursor, foot) <= radius) {
          consider({ kind: 'perpendicular', point: foot, entityId: e.id });
        }
      }
    }
  }

  // 근처점 — 다른 스냅이 하나도 없을 때의 마지막 수단
  if (!best && s.kinds.has('nearest')) {
    for (const e of near) {
      for (const [a, b] of entitySegments(e, scene.blocks)) {
        const p = closestPointOnSegment(cursor, a, b, true);
        if (dist(cursor, p) <= radius) {
          consider({ kind: 'nearest', point: p, entityId: e.id });
        }
      }
    }
  }

  if (!best) return null;
  const found = best as { c: SnapCandidate; d: number };
  return { point: found.c.point, kind: found.c.kind, entityId: found.c.entityId };
}

/* -------------------------------------------------------------------------- *
 * 직교 / 극좌표 구속
 * -------------------------------------------------------------------------- */

/** 기준점에서 수평 또는 수직으로만. 더 긴 축을 따른다. */
export function constrainOrtho(base: Pt, cursor: Pt): Pt {
  const dx = cursor.x - base.x;
  const dy = cursor.y - base.y;
  return Math.abs(dx) >= Math.abs(dy) ? pt(cursor.x, base.y) : pt(base.x, cursor.y);
}

/** 기준점에서 지정 각도의 배수 방향으로만 */
export function constrainPolar(base: Pt, cursor: Pt, stepDeg: number): Pt | null {
  const dx = cursor.x - base.x;
  const dy = cursor.y - base.y;
  const d = Math.hypot(dx, dy);
  if (d === 0) return null;

  const step = (stepDeg * Math.PI) / 180;
  if (step <= 0) return null;

  const a = normalizeAngle(Math.atan2(dy, dx));
  const snapped = Math.round(a / step) * step;
  return polar(base, snapped, d);
}

/* -------------------------------------------------------------------------- *
 * 마커 표기
 * -------------------------------------------------------------------------- */

/** 스냅 마커 글리프. AutoCAD 와 같은 모양을 쓴다 — 사용자가 눈으로 종류를 안다. */
export const SNAP_GLYPH: Record<SnapKind, string> = {
  endpoint: 'square',
  midpoint: 'triangle',
  center: 'circle',
  quadrant: 'diamond',
  intersection: 'cross',
  perpendicular: 'perp',
  insertion: 'insert',
  nearest: 'hourglass',
};

export const SNAP_LABEL: Record<SnapKind, string> = {
  endpoint: '끝점',
  midpoint: '중간점',
  center: '중심',
  quadrant: '사분점',
  intersection: '교차점',
  perpendicular: '직교',
  insertion: '삽입점',
  nearest: '근처점',
};
